import { createSlice } from "@reduxjs/toolkit";
import type { currentSurveySliceState } from "@survey-types/survey.core.types";

// Import modular reducers
import { bingMapReducers } from "./reducers/bingMapReducers";
import { contactReducers } from "./reducers/contactReducers";
import { coreReducers } from "./reducers/coreReducers";
import { customFieldReducers } from "./reducers/customFieldReducers";
import { mapKeyReducers } from "./reducers/mapKeyReducers";
import { mediaReducers } from "./reducers/mediaReducers";
import { polygonReducers } from "./reducers/polygonReducers";
import { propertyReducers } from "./reducers/propertyReducers";
import { sectionReducers } from "./reducers/sectionReducers";

const initialState: currentSurveySliceState = {
  currentSurvey: null,
  isLoading: false,
  error: null,
  editingContactId: null,
  currentPropertyId: null,
  currentPoiId: null,
  mediaFiles: null,
  currentBingMap: null,
  isPropertiesDrawerOpen: false,
  isPOIsDrawerOpen: false,
  isLegendDrawerOpen: false,
  isShareModalOpen: false,
  marketMapKeys: [],
  isStyleEditInProgress: false,
  polygons: [],
  currentPolygonId: null,
  isPolygonDrawingMode: false,
};

const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    // Combine all modular reducers
    ...coreReducers,
    ...sectionReducers,
    ...contactReducers,
    ...propertyReducers,
    ...customFieldReducers,
    ...mediaReducers,
    ...bingMapReducers,
    ...mapKeyReducers,
    ...polygonReducers,
  },
});

// Export individual actions
export const {
  // Core survey state
  setCurrentSurvey,
  updateCurrentSurvey,
  setLoading,
  setError,
  setOpenPropertiesDrawer,
  setOpenPOIsDrawer,
  setShareModalOpen,
  setStyleEditInProgress,

  // Section management
  reorderSections,
  deleteSection,
  updateSectionPdf,
  deleteSectionPdf,
  addSectionPdf,

  // Contact management
  addContact,
  updateContact,
  removeContact,
  reorderContacts,
  setContactEditingId,

  // Property management
  addProperty,
  setProperty,
  updateProperty,
  removeProperty,
  reorderProperties,
  currentPropertyId,
  currentPoiId,

  // Property Field Rule management
  reorderPropertyField,
  updatePropertyFieldRule,
  setPropertyFieldRules,
  updatePropertyFieldRulesBulk,

  // Lease Comp Field Rule management (moved to leaseCompFieldRulesSlice)
  // setLeaseCompFieldRules,
  // reorderLeaseCompField,
  // updateLeaseCompFieldRule,

  // Custom Field management
  addCustomField,
  updateCustomField,
  deleteCustomField,

  //media management
  setMedia,
  setPropertyMedia,
  updateMedia,
  reorderMedia,
  removeMedia,
  moveMedia,

  //BingMap value
  setBingMap,

  setMapKeys,
  addMapKeys,
  deleteMapKey,
  updateToggleLabelMapKey,
  reorderMapKeys,

  // Polygon management
  setPolygons,
  addPolygon,
  updatePolygonInStore,
  deletePolygonFromStore,
  setCurrentPolygonId,
  setPolygonDrawingMode,
  reorderPolygons,
  togglePolygonVisibility,
} = surveySlice.actions;

export default surveySlice.reducer;
